/**
 * ★ WG (28/set/2026) — CONSOLIDAÇÃO dos recebimentos, pura.
 *
 * Existe para servir às DUAS telas com a MESMA regra:
 *
 *   - `/recebimentos` → uma linha por PACIENTE, com o total pago no mês;
 *   - Meu extrato    → uma linha por PACIENTE + FORMA (12 parcelas no cartão
 *     viram uma linha só; medido em setembro: 228 linhas → 78).
 *
 * ★ POR QUE UM MÓDULO, e não a conta dentro de cada tela: a consolidação de Meu
 * extrato tem de valer tanto para a PRÉVIA (calculada no cliente) quanto para o
 * SNAPSHOT do mês fechado (`fechamentos/{mes}/pessoas/{uid}`, gravado por
 * `functions/remuneracao/callables.js`). Consolidar na LEITURA, aqui, alcança os
 * dois — e mantém a peça longe de um deploy de functions, que está travado por
 * causa do `jidDoTelefone` do incidente de 24/set (ver `docs/wg-plano.md` §0).
 * Medido: o snapshot GUARDA a forma (`functions/remuneracao/calculo.js:164`,
 * `forma: r.forma ?? ''`, espalhado por `paraDetalhe`), então consolidar por
 * forma funciona lá também.
 *
 * `web/src/remuneracao/calculo.js` e `web/src/leads/recebimentos.js` não mudaram:
 * este módulo REUSA as regras deles.
 */
import { creditoSdr } from '@/metas/credito';
import { mesDoRecebimento } from '@/remuneracao/calculo';
import { FORMAS_RECEBIMENTO, saldoDoLead } from './recebimentos';

/** Centavos honestos: 0,1 + 0,2 tem de dar 0,3, não 0,30000000000000004. */
const centavos = (v) => Math.round((Number(v) || 0) * 100) / 100;

const paraData = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * ★ O grupo de quem NÃO tem forma gravada.
 *
 * Grupo PRÓPRIO, e nunca somado em `outro`: "Outro" é uma forma de pagamento
 * legítima e escolhida — esconder dado faltante dentro dela transformaria um
 * problema de dado em número aparentemente correto. Medido em 28/set: **0 dos
 * 676** recebimentos está sem forma, e há duas travas contra isso
 * (`recebimentos.js:113` com o padrão e `:122` recusando forma fora do catálogo;
 * o parser do SISO usa `Tipo` como reserva). O grupo existe para o dia em que
 * falhar, não para hoje.
 */
export const FORMA_NAO_INFORMADA = 'nao_informada';

const CATALOGO = new Map(FORMAS_RECEBIMENTO.map((f, i) => [f.id, { ...f, ordem: i }]));

/** A forma de um recebimento, normalizada: vazio → o grupo próprio. */
export const formaDoRecebimento = (recebimento) => {
  const f = String(recebimento?.forma ?? '').trim();
  return f || FORMA_NAO_INFORMADA;
};

/**
 * O rótulo de uma forma.
 *
 * ★ Forma DESCONHECIDA e não vazia (um `cheque` que apareça amanhã) devolve o
 * PRÓPRIO valor, não "não informada": mapear o desconhecido para o rótulo do
 * faltante apagaria o que o banco tem. O princípio do plano — nunca somar em
 * "Outro" — continua valendo nos dois casos.
 */
export const rotuloDaForma = (id) => {
  const f = String(id ?? '').trim();
  if (!f || f === FORMA_NAO_INFORMADA) return 'não informada';
  return CATALOGO.get(f)?.label ?? f;
};

/** A ordem do CATÁLOGO; o grupo do faltante e o desconhecido vão para o fim. */
const ordemDaForma = (id) => CATALOGO.get(id)?.ordem ?? Number.MAX_SAFE_INTEGER;

const porOrdemDeForma = (a, b) =>
  ordemDaForma(a) - ordemDaForma(b) || String(a).localeCompare(String(b), 'pt-BR');

/** Várias formas num texto só, na ordem do catálogo. A ordem da entrada não conta. */
export const rotulosDasFormas = (formas = []) => {
  const lista = [...new Set(formas ?? [])].sort(porOrdemDeForma);
  return lista.length ? lista.map(rotuloDaForma).join(' + ') : '—';
};

/**
 * Os recebimentos de um mês.
 *
 * ★ O mês é decidido por `mesDoRecebimento` — comparação de TEXTO, e nunca uma
 * faixa de datas própria. A consulta que traz estes documentos
 * (`remuneracaoService.js:83`) abre a janela em **±1 dia** de propósito, porque
 * `>=`/`<=` no instante exato dependeria do fuso de quem calcula (navegador em
 * São Paulo × function em UTC). Quem recorta é esta função, sobre o campo `mes`
 * gravado no documento. No relatório real de agosto havia **22 parcelas em 31/08
 * e 1 em 01/09**: errar o recorte move a comissão de 23 pagamentos.
 */
export const doMes = (recebimentos = [], mes) =>
  (recebimentos ?? []).filter((r) => mesDoRecebimento(r) === mes);

/**
 * Uma linha por PACIENTE — a lista de `/recebimentos`.
 *
 * ★ Recebimento cujo lead não está na carteira NÃO desaparece: vira uma linha
 * com nome "—". Sumir com dinheiro porque o lead não veio seria esconder do
 * placar exatamente o caso que precisa de atenção (medido: 2 recebimentos de
 * setembro, R$ 1.080,00, estão em leads sem SDR).
 *
 * @param recebimentos  saída de `buscarRecebimentosDoMes`
 * @param leads         a carteira em memória (`LeadsProvider`)
 * @param mes           'AAAA-MM'
 */
export function porPaciente(recebimentos = [], leads = [], mes) {
  const leadPorId = new Map((leads ?? []).map((l) => [l.id, l]));
  const grupos = new Map();

  for (const r of doMes(recebimentos, mes)) {
    const id = r?.leadId ?? '';
    if (!grupos.has(id)) grupos.set(id, { leadId: id, total: 0, parcelas: 0, formas: new Set() });
    const g = grupos.get(id);
    g.total = centavos(g.total + (Number(r?.valor) || 0));
    g.parcelas += 1;
    g.formas.add(formaDoRecebimento(r));
  }

  const linhas = [...grupos.values()].map((g) => {
    const lead = leadPorId.get(g.leadId);
    const sdrNome = lead ? creditoSdr(lead).nome : '';
    return {
      leadId: g.leadId,
      nome: lead?.nome || '—',
      codigo: String(lead?.codigoCareflow ?? '').trim(),
      total: g.total,
      parcelas: g.parcelas,
      formas: [...g.formas].sort(porOrdemDeForma),
      sdrNome: sdrNome || '',
      // A regra do saldo é a que já existia (`recebimentos.js:59`): só lead
      // `fechado` tem saldo, e nunca negativo. Sem lead, 0.
      saldo: lead ? saldoDoLead(lead) : 0,
    };
  });

  return linhas.sort((a, b) => b.total - a.total || a.nome.localeCompare(b.nome, 'pt-BR'));
}

/** O rodapé de `/recebimentos`: soma do que está NA TELA. */
export const totaisDaLista = (linhas = []) => ({
  pacientes: (linhas ?? []).length,
  parcelas: (linhas ?? []).reduce((t, l) => t + (Number(l.parcelas) || 0), 0),
  total: centavos((linhas ?? []).reduce((t, l) => t + (Number(l.total) || 0), 0)),
  saldo: centavos((linhas ?? []).reduce((t, l) => t + (Number(l.saldo) || 0), 0)),
});

/**
 * Uma linha por PACIENTE + FORMA — a lista de Meu extrato.
 *
 * Entra o `detalheRecebimentos` da linha (da prévia OU do snapshot), cujos itens
 * são `{ leadId, leadNome, data, valor, forma }`. Sai uma linha por par, com a
 * CONTAGEM de parcelas — sem ela, uma linha de R$ 2.600,00 esconderia 12
 * pagamentos e a conferência contra o SISO ficaria impossível (medido: 5 pares
 * com 12 parcelas em setembro, e um com **24**).
 */
export function consolidarPorForma(itens = []) {
  const grupos = new Map();

  for (const i of itens ?? []) {
    const forma = formaDoRecebimento(i);
    const chave = `${i?.leadId ?? ''}::${forma}`;
    if (!grupos.has(chave)) {
      grupos.set(chave, {
        leadId: i?.leadId ?? '',
        leadNome: i?.leadNome || '',
        forma,
        formaLabel: rotuloDaForma(forma),
        parcelas: 0,
        total: 0,
      });
    }
    const g = grupos.get(chave);
    g.parcelas += 1;
    g.total = centavos(g.total + (Number(i?.valor) || 0));
  }

  return [...grupos.values()].sort(
    (a, b) => a.leadNome.localeCompare(b.leadNome, 'pt-BR') || porOrdemDeForma(a.forma, b.forma),
  );
}

/**
 * Ordena por data, da mais antiga para a mais nova.
 *
 * ★ Item SEM data vai para o FIM, nunca para o começo: um registro sem data não
 * pode se disfarçar de "o mais antigo" da lista.
 *
 * ★ Não altera o array recebido — ele pode vir do snapshot, que é a prova do que
 * foi pago e não deve ser mexido nem em memória.
 */
export function ordenarPorDataCrescente(itens = []) {
  return [...(itens ?? [])].sort((a, b) => {
    const da = paraData(a?.data);
    const db = paraData(b?.data);
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return da.getTime() - db.getTime();
  });
}
